import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Users } from "lucide-react";
import { toast } from "sonner";
import { errMsg, fmtDay, useApi, useMe } from "@/lib/api";
import RevenuePanel from "@/components/RevenuePanel";

const PlanTag = ({ u }) => {
  const pro = u.plan === "pro";
  const s = u.subscription;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${pro ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
      {!pro ? "Free" : u.source === "firm" ? "Pro (team member)" : s?.kind === "firm" ? `Team owner - ${s.seats} seats${s?.cancel_at_period_end ? " (ending)" : ""}` : `Pro ${s?.interval === "year" ? "yearly" : "monthly"}${s?.cancel_at_period_end ? " (ending)" : ""}`}
    </span>
  );
};

export default function AdminPage() {
  const { data: me, isLoading: meLoading } = useMe();
  const api = useApi();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: me?.role === "admin",
  });

  if (meLoading) return null;
  if (me?.role !== "admin") return <Navigate to="/" replace />;

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e, "Couldn't change role"));
    }
  };

  const users = (data?.users || []).filter((u) => `${u.email} ${u.name}`.toLowerCase().includes(q.toLowerCase()));
  const proCount = (data?.users || []).filter((u) => u.plan === "pro").length;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="admin-page">
      <RevenuePanel />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3"><Users className="text-blue-600" /> Users</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="admin-summary">
            {data ? `${data.users.length} users - ${proCount} on Pro` : "Loading..."}
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email" className="h-10 pl-8 pr-3 w-full sm:w-64 rounded-md border border-slate-200 bg-white text-sm" />
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="admin-users-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left font-medium px-5 py-3">User</th>
              <th className="text-left font-medium px-5 py-3">Plan</th>
              <th className="text-right font-medium px-5 py-3">This cycle</th>
              <th className="text-right font-medium px-5 py-3">All exports</th>
              <th className="text-left font-medium px-5 py-3">Joined</th>
              <th className="text-left font-medium px-5 py-3">Role</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                <td className="px-5 py-4">
                  <div className="font-semibold">{u.email || "(no email)"}</div>
                  {u.name && <div className="text-xs text-slate-500">{u.name}</div>}
                </td>
                <td className="px-5 py-4"><PlanTag u={u} /></td>
                <td className="px-5 py-4 text-right tabular-nums">{u.plan === "pro" ? u.used : `${u.used}/${u.limit}`}</td>
                <td className="px-5 py-4 text-right tabular-nums">{u.total_exports}</td>
                <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{u.created_at ? fmtDay(u.created_at) : "-"}</td>
                <td className="px-5 py-4">
                  <select
                    data-testid={`admin-role-select-${u.user_id}`}
                    value={u.role}
                    onChange={(e) => setRole(u.user_id, e.target.value)}
                    className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  >
                    {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && users.length === 0 && <div className="py-12 text-center text-slate-500" data-testid="admin-empty">No users found.</div>}
      </div>
    </main>
  );
}
